<script lang="ts" setup>
  import { ref, onMounted, type PropType, type Ref, onBeforeUnmount, onUpdated } from 'vue';
  import type { SwiperOptions } from 'swiper/types';
  import type { NavButtons } from '@storefront/types';
  import Swiper from 'swiper';
  import { Navigation, Autoplay } from 'swiper/modules';
  import 'swiper/css';
  import 'swiper/css/navigation';

  type Brand = {
    id: number;
    name: string;
    slug: string;
    photo: string | null;
  }

  const props = defineProps({
    brands: { type: Array as PropType<Brand[]>, require: true },
    parameters: { type: Object as PropType<SwiperOptions>, required: false, default: {} },
    enableAutoplay: { type: Boolean, required: false, default: false },
    timeAutoplay: { type: [Number, String] as PropType<number | string>, required: false, default: 3000 },
    speed: { type: [Number, String] as PropType<number | string>, required: false, default: 400 },
  });

  const htmlElement = ref() as Ref<HTMLElement>;
  const htmlNavigation = ref() as Ref<NavButtons>;
  const swiper = ref() as Ref<Swiper>;

  function initializeSwipers() {
    swiper.value = new Swiper(htmlElement.value, {
      ...props.parameters,
      modules: [Navigation, Autoplay],
      autoplay: props.enableAutoplay ? { delay: Number(props.timeAutoplay ?? 3000) } : false,
      speed: Number(props.speed ?? 400),
      navigation: {
        nextEl: htmlNavigation.value.next.htmlElement,
        prevEl: htmlNavigation.value.prev.htmlElement,
      },
    });
  }

  function destroySwipers() {
    swiper.value?.destroy();
  }

  onMounted(() => {
    initializeSwipers();
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });

  onUpdated(() => {
    initializeSwipers();
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });
</script>
<template>
  <div class="items-carousel brands-carousel">
    <div ref="htmlElement" class="swiper items-carousel__container brands-carousel__container">
      <div class="swiper-wrapper">
        <div v-for="brand in brands" class="swiper-slide items-carousel__slide brands-carousel__article">
          <a class="brands-carousel__link" :href="$route('web.brand.show', { brand: brand.slug })" :aria-label="brand.name">
            <MediaPicture :photo="brand.photo" class="brands-carousel__picture" loading="lazy" sizes="300x200 2x, 150x100" type="logo" />
          </a>
          <div class="swiper-lazy-preloader" />
        </div>
      </div>
    </div>
    <SfCarouselButtons ref="htmlNavigation" />
  </div>
</template>
<style lang="scss" scoped>
  .brands-carousel {
    &:deep(.brands-carousel__picture) {
      width: 100%;

      img {
        aspect-ratio: 3/2;
      }
    }

    &__link {
      align-content: center;
      width: 100%;

      &:focus-visible {
        outline-offset: -2px;
      }
    }
  }
</style>
